import type {
  CivilAdviseSaveStep1,
  CivilAdviseSaveStep2,
  CivilAdviseSaveStep3,
  CivilAdviseSaveStep4,
  CivilAdviseSaveStep4File,
  CivilAdviseStepsResponse,
  SaveCivilAdviseStepsPayload,
} from '../types/civilAdviseApi';
import type { CivilAdviseAdviceFileRow, CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { CivilAdviseWizardStep } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import { FILE_CATEGORY_CIVIL_ADVISE } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';
import { buildInstituteApiPayload, mapInstituteFromApiResponse } from '../utils/instituteApiPayload';

export type { CivilAdviseWizardStep };

export function caseRequestIdFromResponse(response: CivilAdviseStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: CivilAdviseStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function step1LooksComplete(s1: CivilAdviseSaveStep1 | undefined, response: CivilAdviseStepsResponse): boolean {
  const subId = (s1?.sub_category_id ?? response.file_sub_category_id ?? '').trim();
  const desc = (s1?.description ?? '').trim();
  const extRef = (s1?.external_case_reference_number ?? '').trim();
  return Boolean(subId && desc && extRef);
}

function step2LooksComplete(s2: CivilAdviseSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.urgency_level_id ?? '').trim() ||
      (s2?.deadline ?? '').trim() ||
      (s2?.urgency_justification ?? '').trim(),
  );
}

function step3LooksComplete(s3: CivilAdviseSaveStep3 | undefined): boolean {
  return Boolean((s3?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step4LooksComplete(s4: CivilAdviseSaveStep4 | undefined): boolean {
  const files = (s4?.uploaded_files?.length ? s4.uploaded_files : s4?.documents) ?? [];
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

function stepProcessed(
  response: CivilAdviseStepsResponse,
  camel: keyof CivilAdviseStepsResponse,
  snake: keyof CivilAdviseStepsResponse,
): boolean | undefined {
  const c = response[camel];
  if (typeof c === 'boolean') return c;
  const s = response[snake];
  if (typeof s === 'boolean') return s;
  return undefined;
}

function step2HasAnyData(s2: CivilAdviseSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.urgency_level_id ?? '').trim() ||
      (s2?.deadline ?? '').trim() ||
      (s2?.urgency_justification ?? '').trim(),
  );
}

function step3HasAnyData(s3: CivilAdviseSaveStep3 | undefined): boolean {
  return Boolean((s3?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step4HasAnyData(s4: CivilAdviseSaveStep4 | undefined): boolean {
  const files = (s4?.uploaded_files?.length ? s4.uploaded_files : s4?.documents) ?? [];
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

function latestSavedCivilAdviseStep(response: CivilAdviseStepsResponse): CivilAdviseWizardStep {
  if (step4HasAnyData(response.step_4)) return 4;
  if (step3HasAnyData(response.step_3)) return 3;
  if (step2HasAnyData(response.step_2)) return 2;
  if (step1LooksComplete(response.step_1, response)) return 1;
  if (
    (response.step_1?.description ?? '').trim() ||
    (response.step_1?.sub_category_id ?? response.file_sub_category_id ?? '').trim() ||
    (response.step_1?.external_case_reference_number ?? '').trim()
  ) {
    return 1;
  }
  return 1;
}

export function resolveCivilAdviseDraftResumeStep(response: CivilAdviseStepsResponse): CivilAdviseWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (stepProcessed(response, 'step1Processed', 'step_1_processed') === false) return 1;
  if (stepProcessed(response, 'step2Processed', 'step_2_processed') === false) return 2;
  if (stepProcessed(response, 'step3Processed', 'step_3_processed') === false) return 3;
  if (stepProcessed(response, 'step4Processed', 'step_4_processed') === false) return 4;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;

  if (!step1LooksComplete(s1, response)) {
    const latest = latestSavedCivilAdviseStep(response);
    return latest > 1 ? latest : 1;
  }
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  return 4;
    },
    (step) => step >= 1 && step <= 4,
  );
}

function connectedCaseRelevantFromStep1(s1: CivilAdviseSaveStep1 | undefined): boolean | undefined {
  if (s1?.has_connected_case_relevant_to_advice_sought != null) {
    return s1.has_connected_case_relevant_to_advice_sought;
  }
  return s1?.has_connected_case_relevant_to_advice;
}

function connectedCaseDetailsFromStep1(s1: CivilAdviseSaveStep1 | undefined): string | undefined {
  const v = s1?.case_details_aspect ?? s1?.relevent_case_details_aspect;
  return typeof v === 'string' ? v : undefined;
}

function previousAdviceDocumentsFromStep1(s1: CivilAdviseSaveStep1 | undefined): SavedFileRow[] {
  const docs = s1?.previous_advice_documents ?? s1?.documents ?? [];
  return docs.filter((f) => Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()));
}

function step4DocumentsFromApi(s4: CivilAdviseSaveStep4 | undefined): CivilAdviseSaveStep4File[] {
  return [
    ...(s4?.uploaded_files ?? []),
    ...(s4?.extra_uploaded_files ?? []),
    ...(s4?.documents ?? []),
  ];
}

function apiDocumentTypeId(file: CivilAdviseSaveStep4File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function apiDocumentHasFile(file: CivilAdviseSaveStep4File): boolean {
  return Boolean((file.file_name ?? '').trim() || (file.original_file_name ?? '').trim());
}

function isPersistedApiDocument(file: CivilAdviseSaveStep4File): boolean {
  const typeId = apiDocumentTypeId(file);
  const hasContent = apiDocumentHasFile(file) || Boolean((file.reason ?? '').trim());
  if (!hasContent) return false;
  return typeId ? isDocumentTypeUuid(typeId) : true;
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
  upload_time?: string | null;
  uploadTime?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function uploadTimeFromSavedFile(file: SavedFileRow): string {
  const raw = file.upload_time ?? file.uploadTime;
  return typeof raw === 'string' ? raw.trim() : '';
}

function step4FileFields(file: CivilAdviseSaveStep4File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step4FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep3(s3: CivilAdviseSaveStep3 | undefined): string {
  const raw = s3?.other_method ?? s3?.otherMethod;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s3: CivilAdviseSaveStep3 | undefined): string {
  const raw = s3?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(
  response: CivilAdviseStepsResponse,
  s1: CivilAdviseSaveStep1 | undefined,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  void s1;
  return prev;
}

function resolveApplicableAreaNames(
  response: CivilAdviseStepsResponse,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function mapStep1(draft: CivilAdviseWizardDraft): SaveStepFields<CivilAdviseSaveStep1> {
  const previous_advice_documents = draft.previousAdviceFiles
    .map((f) => {
      const names = step4FileNamesForSave(strOrNull(f.fileName), strOrNull(f.originalFileName));
      if (!names.file_name && !names.original_file_name) return null;
      return names;
    })
    .filter((f): f is { file_name?: string; original_file_name?: string } => Boolean(f));

  const step1: SaveStepFields<CivilAdviseSaveStep1> = {
    description: strOrNull(draft.description),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    province_id: strOrNull(draft.provinceId),
    district_id: strOrNull(draft.districtId),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    has_previous_advice_received: yesNoToBool(draft.hasPreviousAdvice),
    has_connected_case_relevant_to_advice: yesNoToBool(draft.hasConnectedCaseRelevant),
    relevent_case_details_aspect:
      draft.hasConnectedCaseRelevant === 'yes' ? strOrNull(draft.connectedCaseDetails) : null,
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedAgdFileRef) : undefined,
  };

  // Preserve historical previous-advice attachments whenever they remain on the draft.
  // Never send an empty list — omitting the field avoids clearing intake history on update.
  if (previous_advice_documents.length) {
    step1.previous_advice_documents = previous_advice_documents;
  }

  return step1;
}

function mapStep2(draft: CivilAdviseWizardDraft): SaveStepFields<CivilAdviseSaveStep2> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep3(draft: CivilAdviseWizardDraft): SaveStepFields<CivilAdviseSaveStep3> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step3: CivilAdviseSaveStep3 = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step3.other_method = strOrNull(draft.deliveryOther);
  }

  return step3;
}

function mapStep4(draft: CivilAdviseWizardDraft): SaveStepFields<CivilAdviseSaveStep4> {
  const uploaded_files = draft.documents
  .filter(
      (d) => {
        const files = Array.isArray(d.files) ? d.files : [];
        return isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (files.length > 0 || strOrNull(d.skipReason));
    })
    .flatMap((d) => {
      const files = Array.isArray(d.files) ? d.files : [];
      const reason = strOrNull(d.skipReason);
        const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
        if (files.length === 0) {
          return [{ ...(documentTypeId ? { document_type_id: documentTypeId } : {}), ...(reason ? { reason } : {}) }];
        }
        return files.map((f) => {
          const names = step4FileNamesForSave(strOrNull(f.fileName), strOrNull(f.originalFileName));
          return {
            ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
            ...names,
            ...(reason ? { reason } : {}),
          };
        });
      });
  const extra_uploaded_files = draft.documents
    .filter((d) => !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId))
    .flatMap((d) => {
      const files = Array.isArray(d.files) ? d.files : [];
      const reason = strOrNull(d.skipReason);
      if (files.length === 0 && !reason) return [];
      if (files.length === 0) return [{ ...(reason ? { reason } : {}) }];
      return files.map((f) => {
        const names = step4FileNamesForSave(strOrNull(f.fileName), strOrNull(f.originalFileName));
        return { ...names, ...(reason ? { reason } : {}) };
      });
    });
  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveCivilAdvisePayload(
  draft: CivilAdviseWizardDraft,
  options: {
    currentStep?: CivilAdviseWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveCivilAdviseStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveCivilAdviseStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;
  Object.assign(payload, buildInstituteApiPayload(draft));

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapCivilAdviseStepsToDraft(
  response: CivilAdviseStepsResponse,
  prev: CivilAdviseWizardDraft,
): CivilAdviseWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;

  const fileCategoryId =
    response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId;
  const fileCategoryName =
    response.file_category_name ?? s1?.category_name ?? prev.fileCategory;
  const subFileCategoryId =
    response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName;

  const previousAdviceFiles = (() => {
    const fromApi = previousAdviceDocumentsFromStep1(s1);
    if (!fromApi.length) return prev.previousAdviceFiles;
    return fromApi
      .map((f): CivilAdviseAdviceFileRow | null => {
        const { fileName, originalFileName } = savedFileFields(f);
        if (!fileName && !originalFileName) return null;
        const storageKey = fileName || originalFileName;
        return {
          id: storageKey,
          fileName: storageKey,
          originalFileName: originalFileName || fileName,
          uploadTime: uploadTimeFromSavedFile(f),
        };
      })
      .filter((f): f is CivilAdviseAdviceFileRow => f !== null);
  })();

  const documents = (() => {
    const fromApi = step4DocumentsFromApi(s4).filter(isPersistedApiDocument);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      const grouped = new Map<string, CivilAdviseSaveStep4File[]>();
      for (const f of fromApi) {
        const typeId = apiDocumentTypeId(f);
        const isValid = typeId ? isDocumentTypeUuid(typeId) : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        if (!isValid) continue;
        const key = typeId || `__no_type__${grouped.size}`;
        const list = grouped.get(key) ?? [];
        list.push(f);
        grouped.set(key, list);
      }

      return Array.from(grouped.entries()).map(([typeId, entries]) => {
        const files = entries
          .map((f) => {
            const { fileName, originalFileName } = step4FileFields(f);
            return fileName ? { fileName, originalFileName, uploadTime: f.upload_time ?? '' } : null;
          })
          .filter((f): f is { fileName: string; originalFileName: string; uploadTime: string } => f !== null);

        const first = entries[0];
        return {
          id: (first.case_request_document_id ?? '').trim() || crypto.randomUUID(),
          checklistItemId: '',
          documentTypeId: typeId.startsWith('__no_type__') ? '' : typeId,
          documentName: first.document_type_name?.trim() || 'Document',
          mandatory: false,
          files,
          skipReason: entries.find((e) => (e.reason ?? '').trim())?.reason ?? '',
        };
      });
    }
    return prev.documents.map((doc) => {
      const matches = fromApi.filter((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        if (
          !isDocumentTypeUuid(doc.documentTypeId) &&
          f.document_type_name &&
          doc.documentName &&
          f.document_type_name.trim().toLowerCase() === doc.documentName.trim().toLowerCase()
        ) return true;
        return false;
      });
      if (!matches.length) return doc;
      const files = matches
        .map((match) => {
          const { fileName, originalFileName } = step4FileFields(match);
          return fileName ? { fileName, originalFileName, uploadTime: match.upload_time ?? '' } : null;
        })
        .filter((f): f is { fileName: string; originalFileName: string; uploadTime: string } => f !== null);
      const skipReason = matches[0].reason ?? doc.skipReason;
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(matches[0]) || doc.documentTypeId,
        files: files.length > 0 ? files : (Array.isArray(doc.files) ? doc.files : []),
        skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(s3?.physical_file_delivery_methods);
  const deliveryIds = s3?.physical_file_delivery_method_ids?.length
    ? s3.physical_file_delivery_method_ids
    : delivery.ids;

  return {
    ...prev,
    ...mapInstituteFromApiResponse(response, prev),
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategoryId,
    fileCategory: fileCategoryName || FILE_CATEGORY_CIVIL_ADVISE,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, s1, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: s1?.description ?? prev.description,
    provinceId: s1?.province_id ?? prev.provinceId,
    provinceName: s1?.province_name ?? prev.provinceName,
    districtId: s1?.district_id ?? prev.districtId,
    districtName: s1?.district_name ?? prev.districtName,
    yourReferenceNumber: s1?.external_case_reference_number ?? prev.yourReferenceNumber,
    otherExternalReferences: s1?.other_external_case_reference_number ?? prev.otherExternalReferences,
    hasPreviousAdvice: boolToYesNo(s1?.has_previous_advice_received) || prev.hasPreviousAdvice,
    previousAdviceFiles,
    hasConnectedCaseRelevant:
      boolToYesNo(connectedCaseRelevantFromStep1(s1)) || prev.hasConnectedCaseRelevant,
    connectedCaseDetails: connectedCaseDetailsFromStep1(s1) ?? prev.connectedCaseDetails,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    hasConnectedMatters: boolToYesNo(s1?.has_connected_case) || prev.hasConnectedMatters || 'no',
    connectedCaseNumber: s1?.connected_case_number ?? prev.connectedCaseNumber,
    connectedAgdFileRef: s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef,
    urgencyLevelId: s2?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s2?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s2?.deadline ?? prev.deadline,
    urgencyJustification: s2?.urgency_justification ?? prev.urgencyJustification,
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther: otherMethodFromStep3(s3) || otherMethodFromDeliveryRows(s3) || prev.deliveryOther,
    documents,
  };
}
